/**
 * MLEADER CONTEXT_DATA byte-faithful emit (export side) — the symmetric inverse
 * of {@link MLeaderContextBuilder} (import side, `mleaderContextBuilder.ts`).
 *
 * Re-emits the AcDbMLeader `CONTEXT_DATA` record from the TYPED nested tree the
 * parser decoded — `contextData` (ezdxf MLeaderContext header + INLINE MTextData
 * /BlockData by the 290/296 flags + plane + R2010 attach trailer), `leaderRoots`
 * (the per-LEADER / per-LEADER_LINE hierarchy with its sub-slice-A typed fields)
 * and `rawContextGroups` (every in-context group the parser could not promote to
 * a typed field, preserved verbatim) — using the REAL ezdxf/AutoCAD structural
 * markers `300 "CONTEXT_DATA{"` … `301 "}"`, `302 "LEADER{"` … `303 "}"`,
 * `304 "LEADER_LINE{"` … `305 "}"`. This REPLACES the legacy synthesised
 * CONTEXT_DATA (which dropped the header / MTextData / BlockData / plane / per-
 * leader metadata and opened leader lines with the wrong `302 "LEADER_LINE{"`
 * legacy marker), closing the MLEADER nested-tree dataloss (ПРИОРИТЕТ #1).
 *
 * Emit order is the ezdxf MULTILEADER field order (`tmp_smleader_codemap.md`
 * Scopes 1-5): an "always" field is emitted whenever its typed value is present,
 * an "optional" field only when present, so a parse → write → parse round-trip
 * is byte/structurally stable.
 *
 * Returns RAW `{ code, value }` entries (numbers stay numbers) so the single
 * `writer.ts` `pushGroup` performs the one DXF number formatting — this module
 * imports NO writer.ts helper, keeping the module a leaf (no dependency cycle).
 *
 * FALLBACK: when `contextData` is absent (a marker-less MLEADER, or the bridge
 * export path which does not yet project contextData — sub-slice D), it emits
 * the minimal legacy-compatible context (textStyleHandle + the leaderRoots
 * hierarchy) so that path round-trips unchanged — but still with the REAL
 * 304/305 leader-line markers.
 */
import type {
  DxfMLeaderBlockData,
  DxfMLeaderBreakPoint,
  DxfMLeaderContext,
  DxfMLeaderEntity,
  DxfMLeaderLeaderLine,
  DxfMLeaderLeaderNode,
  DxfMLeaderMTextData,
  DxfPoint3D,
} from "./types";

/** A not-yet-formatted DXF group; `writer.ts` pushGroup formats the number. */
export type RawGroup = { code: number; value: string | number };

type MLeaderGeometry = DxfMLeaderEntity["geometry"];

class RawGroupSink {
  readonly out: RawGroup[] = [];

  push(code: number, value: string | number): void {
    this.out.push({ code, value });
  }

  /** A defined numeric/string scalar. */
  opt(code: number, value: number | string | undefined): void {
    if (value !== undefined) this.push(code, value);
  }

  /** A defined boolean → 1/0. */
  bool(code: number, value: boolean | undefined): void {
    if (value !== undefined) this.push(code, value ? 1 : 0);
  }

  /** A 3D point spread across its X/Y/Z codes. */
  point(codes: [number, number, number], p: DxfPoint3D | undefined): void {
    if (!p) return;
    this.push(codes[0], p.x);
    this.push(codes[1], p.y);
    this.push(codes[2], p.z);
  }
}

/** SCOPE 2 — MTextData inline (no own brace), ezdxf emit order. */
function emitMTextData(s: RawGroupSink, m: DxfMLeaderMTextData): void {
  s.opt(304, m.defaultContent);
  s.point([11, 21, 31], m.extrusion);
  s.opt(340, m.styleHandle);
  s.point([12, 22, 32], m.insert);
  s.point([13, 23, 33], m.textDirection);
  s.opt(42, m.rotation);
  s.opt(43, m.width);
  s.opt(44, m.definedHeight);
  s.opt(45, m.lineSpacingFactor);
  s.opt(170, m.lineSpacingStyle);
  s.opt(90, m.color);
  s.opt(171, m.alignment);
  s.opt(172, m.flowDirection);
  s.opt(91, m.backgroundColor);
  s.opt(141, m.backgroundScaleFactor);
  s.opt(92, m.backgroundTransparency);
  s.bool(291, m.useWindowBackground);
  s.bool(292, m.hasBackgroundFill);
  s.opt(173, m.columnType);
  s.bool(293, m.useAutoHeight);
  s.opt(142, m.columnWidth);
  s.opt(143, m.columnGutter);
  s.bool(294, m.columnFlowReversed);
  for (const size of m.columnSizes ?? []) s.push(144, size);
  s.bool(295, m.useWordBreak);
}

/** SCOPE 3 — BlockData inline (no own brace), ezdxf emit order. */
function emitBlockData(s: RawGroupSink, b: DxfMLeaderBlockData): void {
  s.opt(341, b.blockRecordHandle);
  s.point([14, 24, 34], b.extrusion);
  s.point([15, 25, 35], b.insert);
  s.point([16, 26, 36], b.scale);
  s.opt(46, b.rotation);
  s.opt(93, b.color);
  for (const v of b.matrix ?? []) s.push(47, v);
}

/** LeaderData breaks — alternating 12↔13 pairs (start uses 12/22/32, end uses
 * 13/23/33), no break index. */
function emitLeaderBreaks(s: RawGroupSink, breaks: DxfMLeaderBreakPoint[]): void {
  for (const bp of breaks) {
    s.point([12, 22, 32], bp.start);
    s.point([13, 23, 33], bp.end);
  }
}

/** LeaderLine breaks — 90 break_index + 11/21/31 start + 12/22/32 end. */
function emitLeaderLineBreaks(
  s: RawGroupSink,
  breaks: DxfMLeaderBreakPoint[],
): void {
  for (const bp of breaks) {
    s.opt(90, bp.breakIndex);
    s.point([11, 21, 31], bp.start);
    s.point([12, 22, 32], bp.end);
  }
}

/** SCOPE 5 — LeaderLine (304{…305}), ezdxf emit order. */
function emitLeaderLine(s: RawGroupSink, line: DxfMLeaderLeaderLine): void {
  s.push(304, "LEADER_LINE{");
  for (const v of line.vertices) s.point([10, 20, 30], v);
  if (line.breakPoints) emitLeaderLineBreaks(s, line.breakPoints);
  s.opt(91, line.leaderLineIndex);
  s.opt(92, line.colorOverride);
  s.opt(340, line.lineTypeHandle);
  s.opt(170, line.lineType);
  s.opt(171, line.lineWeightOverride);
  // Codex #268 — the verbatim in-frame remainder, re-emitted INSIDE the 304…305
  // frame (restoring its source structural position) rather than flat at the end
  // of CONTEXT_DATA, so AutoCAD/ezdxf keep the leader-line frame association.
  for (const g of line.rawGroups ?? []) s.push(g.code, g.value);
  s.push(305, "}");
}

/** SCOPE 4 — LeaderData (302{…303}), ezdxf emit order. */
function emitLeader(s: RawGroupSink, node: DxfMLeaderLeaderNode): void {
  s.push(302, "LEADER{");
  s.bool(290, node.hasLastLeaderLine);
  s.bool(291, node.hasDoglegVector);
  s.point([10, 20, 30], node.lastLeaderPoint);
  s.point([11, 21, 31], node.doglegVector);
  if (node.breakPoints) emitLeaderBreaks(s, node.breakPoints);
  s.opt(90, node.leaderIndex);
  s.opt(40, node.doglegLength);
  for (const line of node.lines) emitLeaderLine(s, line);
  s.opt(271, node.attachmentDirection);
  // Codex #268 — the verbatim in-frame remainder for THIS LEADER (outside its
  // LEADER_LINE sub-frames), re-emitted INSIDE the 302…303 frame rather than flat
  // at the end of CONTEXT_DATA, preserving leader-frame association.
  for (const g of node.rawGroups ?? []) s.push(g.code, g.value);
  s.push(303, "}");
}

/** SCOPE 1 — MLeaderContext header + inline MTextData/BlockData + plane. */
function emitContextHeader(s: RawGroupSink, ctx: DxfMLeaderContext): void {
  s.opt(40, ctx.scale);
  s.point([10, 20, 30], ctx.basePoint);
  s.opt(41, ctx.charHeight);
  s.opt(140, ctx.arrowHeadSize);
  s.opt(145, ctx.landingGapSize);
  s.opt(174, ctx.textLeftAttachment);
  s.opt(175, ctx.textRightAttachment);
  s.opt(176, ctx.textAlignType);
  s.opt(177, ctx.attachmentType);
  // 290 has_mtext → MTextData inline; 296 has_block → BlockData inline.
  // #294 r2 — emitted only when DEFINED (absence-preserving, like every other
  // header field): the builder no longer pre-initialises them to false, so
  // `undefined` means the source omitted the group and re-emitting `290 0` /
  // `296 0` would invent groups on a raw-only CONTEXT_DATA block. A parsed
  // explicit `290 0` / `296 0` still round-trips verbatim (#294 r3 removed
  // the bridge primary schema's defaults too, so a sparse PRIMARY header no
  // longer materializes these booleans either).
  s.bool(290, ctx.hasMText);
  if (ctx.hasMText && ctx.mTextData) emitMTextData(s, ctx.mTextData);
  s.bool(296, ctx.hasBlock);
  if (ctx.hasBlock && ctx.blockData) emitBlockData(s, ctx.blockData);
  // Plane + normal-reversed trailer.
  s.point([110, 120, 130], ctx.planeOrigin);
  s.point([111, 121, 131], ctx.planeXAxis);
  s.point([112, 122, 132], ctx.planeYAxis);
  s.bool(297, ctx.planeNormalReversed);
}

/**
 * FIX (#432 SL-5 r8, Finding 1) — materialize the flat MTextData-homed geometry
 * fields that have NO body-level fallback emit into a MINIMAL MTextData for the
 * marker-less fallback path. Line-spacing (SL-5 P3c removed the out-of-spec body
 * 48/49) and text rotation (body 42 is arrow_head_size, so textRotation is never
 * body-emitted) live ONLY inside CONTEXT_DATA / MTextData (ezdxf `MTextData`
 * `line_spacing_factor`=45, `line_spacing_style`=170, `rotation`=42). A hand-
 * built / bridge-less dxf-io MLEADER carrying non-default values but NO
 * contextData therefore DROPPED them (saved+reopened as ezdxf defaults). Build a
 * partial MTextData carrying only the non-default such fields so `emitMTextData`
 * (the single MTextData emit-order truth) re-emits them under `290 1`; the
 * symmetric context builder reads 42/45/170 back into contextData.mTextData, so
 * the next faithful export re-emits them. Returns undefined when every field is
 * default/absent (no spurious MTextData on a plain MLEADER). Defaults per ezdxf
 * `MTextData.__init__`: rotation 0.0, line_spacing_factor 1.0, line_spacing_style
 * 1.
 */
function buildFallbackMTextData(
  geom: MLeaderGeometry,
  // #432 r11 (@codex) — when the flat line-spacing was PROMOTED from a legacy body
  // `48`/`49` that is STILL parked in extraGroups (a pure dxf-io parse of an old
  // Edit-DWG marker-less MLEADER), the writer re-emits those 48/49 at the entity
  // tail. Materializing MTextData 45/170 here TOO would DOUBLE the line-spacing
  // (48/49 + 45/170). Skip 45/170 in that case so the legacy round-trip stays
  // byte-faithful (48/49 verbatim); a marker-less MLEADER whose line-spacing came
  // from typed flat fields (no legacy 48/49) still materializes 45/170. Rotation
  // (42) has no legacy body equivalent and is always materialized.
  suppressLineSpacing: boolean,
): DxfMLeaderMTextData | undefined {
  const m: DxfMLeaderMTextData = {};
  if (geom.textRotation !== undefined && geom.textRotation !== 0) {
    m.rotation = geom.textRotation; // 42
  }
  if (
    !suppressLineSpacing &&
    geom.textLineSpacingFactor !== undefined &&
    geom.textLineSpacingFactor !== 1
  ) {
    m.lineSpacingFactor = geom.textLineSpacingFactor; // 45
  }
  if (
    !suppressLineSpacing &&
    geom.textLineSpacingStyle !== undefined &&
    geom.textLineSpacingStyle !== 1
  ) {
    m.lineSpacingStyle = geom.textLineSpacingStyle; // 170
  }
  return m.rotation !== undefined ||
    m.lineSpacingFactor !== undefined ||
    m.lineSpacingStyle !== undefined
    ? m
    : undefined;
}

/**
 * Build the full CONTEXT_DATA group sequence (`300 "CONTEXT_DATA{"` … `301 "}"`)
 * for an MLEADER from its typed geometry. The caller (`writer.ts`) pushes each
 * returned entry through `pushGroup` so numbers are DXF-formatted once.
 */
export function buildMLeaderContextGroups(
  geom: MLeaderGeometry,
  // #432 r11 (@codex) — legacy body 48/49 line-spacing is still parked in
  // entity.extraGroups (the writer re-emits it at the tail); suppress the fallback
  // MTextData 45/170 materialization to avoid a duplicate. See buildFallbackMTextData.
  legacyLineSpacingInExtraGroups = false,
): RawGroup[] {
  const s = new RawGroupSink();
  s.push(300, "CONTEXT_DATA{");

  const ctx = geom.contextData;
  if (ctx) {
    emitContextHeader(s, ctx);
    // FIX 1 (sub-slice D follow-up) — legacy in-context text-style handle stamp.
    // The faithful path emits group 340 from contextData.mTextData.styleHandle
    // (inside emitMTextData). A SUBSTANTIVE context that carries NO MTextData
    // (e.g. the degenerate #243 "marker-only" context the widened substantive
    // gate now routes through this faithful path) would otherwise drop the
    // legacy group-340 stamp the marker-less fallback emits. Re-emit it from the
    // flat `textStyleHandle` so the #243 textStyle round-trip survives the gate
    // widening, positioned in the header region (before the leaders) exactly
    // like the fallback path.
    const ctxStyleHandle = ctx.mTextData?.styleHandle;
    const hasTypedStyleHandle =
      ctxStyleHandle !== undefined && ctxStyleHandle !== "";
    const legacyStyleHandle =
      !hasTypedStyleHandle && geom.textStyleHandle
        ? geom.textStyleHandle
        : undefined;
    if (legacyStyleHandle !== undefined) s.push(340, legacyStyleHandle);
    for (const root of geom.leaderRoots) {
      for (const node of root.nodes) emitLeader(s, node);
    }
    // R2010 top/bottom attach trailer (after the leaders, before close).
    s.opt(272, ctx.topAttachment);
    s.opt(273, ctx.bottomAttachment);
    // Any in-context group the parser could not type — verbatim, original order.
    // EXCEPTION: a group-340 parked here is the in-context text-style handle —
    // the parser DUAL-captures the in-context 340 onto BOTH `textStyleHandle`
    // AND rawContextGroups (a real MTextData 340 is consumed into
    // mTextData.styleHandle, never parked). It is emitted authoritatively via
    // the typed mTextData / textStyleHandle channel above, so when that channel
    // owns it, suppress the verbatim copy — preventing a double 340 and letting
    // an EDITED style export the fresh handle rather than the stale parked one.
    const styleHandleOwnedByTypedChannel =
      hasTypedStyleHandle || legacyStyleHandle !== undefined;
    for (const g of geom.rawContextGroups ?? []) {
      if (g.code === 340 && styleHandleOwnedByTypedChannel) continue;
      s.push(g.code, g.value);
    }
  } else {
    // FALLBACK (marker-less / bridge export): minimal legacy-compatible context.
    // The in-context text-style handle is re-emitted from the flat field (no
    // MTextData container exists here); when contextData IS present it instead
    // lives inside MTextData.styleHandle (emitted above) — never both.
    if (geom.textStyleHandle) s.push(340, geom.textStyleHandle);
    // 145 landing_gap_size — the landing gap's ONLY DXF home is CONTEXT_DATA
    // (ezdxf MLeaderContext.landing_gap_size; gap-C.2 removed the bogus body 44).
    // A model-authored / edited MLEADER takes this fallback (no parsed context),
    // so without emitting 145 here a non-default landing gap is DROPPED and
    // re-imports as the default 2 (Codex #271). The bridge sets geom.landingGap
    // only when non-default, so this never bloats a default MLEADER.
    s.opt(145, geom.landingGap);
    // #432 r8 Finding 1 — materialize a minimal MTextData (`290 1` has_mtext →
    // inline 42 rotation / 45 line_spacing_factor / 170 line_spacing_style) for
    // the flat MTextData-homed fields that have NO body-level fallback emit, so a
    // non-default value reaches DXF (its only ezdxf home) and round-trips through
    // the symmetric context builder instead of being silently dropped. Placed in
    // the ezdxf header position (after 145, before the leaders); ezdxf reads a
    // partial MTextData fine (unset fields keep MTextData defaults). See
    // buildFallbackMTextData.
    const fallbackMText = buildFallbackMTextData(
      geom,
      legacyLineSpacingInExtraGroups,
    );
    if (fallbackMText) {
      s.push(290, 1); // has_mtext → inline MTextData follows
      emitMTextData(s, fallbackMText);
    }
    for (const root of geom.leaderRoots) {
      for (const node of root.nodes) emitLeader(s, node);
    }
  }

  s.push(301, "}");
  return s.out;
}

/** Emit ONE CONTEXT_DATA block (300{…301}) from a typed context + leader roots —
 * used for the annotative SECONDARY blocks. The geom-level LEGACY style-handle
 * stamp belongs to the PRIMARY block only, so this leaner builder emits the
 * header + leader hierarchy + R2010 trailer. Codex #285-9: it ALSO re-emits THIS
 * secondary block's OWN `rawContextGroups` (verbatim, in source order, after the
 * trailer — the same position the primary writer uses) so a raw group that lived
 * inside a secondary CONTEXT_DATA round-trips in that block instead of leaking
 * into the primary (the parser now keeps secondary raw groups per-block). */
function buildSecondaryContextBlock(
  ctx: DxfMLeaderContext,
  leaderRoots: ReadonlyArray<DxfMLeaderEntity["geometry"]["leaderRoots"][number]>,
  rawContextGroups: ReadonlyArray<{ code: number; value: string | number }> | undefined,
): RawGroup[] {
  const s = new RawGroupSink();
  s.push(300, "CONTEXT_DATA{");
  emitContextHeader(s, ctx);
  for (const root of leaderRoots) {
    for (const node of root.nodes) emitLeader(s, node);
  }
  s.opt(272, ctx.topAttachment);
  s.opt(273, ctx.bottomAttachment);
  for (const g of rawContextGroups ?? []) s.push(g.code, g.value);
  s.push(301, "}");
  return s.out;
}

/**
 * Build EVERY CONTEXT_DATA block for an MLEADER: the primary
 * ({@link buildMLeaderContextGroups}) followed by one block per annotative
 * SECONDARY context. An annotative multileader carries multiple CONTEXT_DATA
 * records (one per scale); the parser keeps them all typed (`secondaryContexts`)
 * and this re-emits them all instead of dropping every block after the first
 * (Codex #268). A single-context MLEADER returns exactly the primary block, so
 * the exactly-one-CONTEXT_DATA fidelity contract is preserved.
 */
export function buildAllMLeaderContextGroups(
  geom: MLeaderGeometry,
  legacyLineSpacingInExtraGroups = false,
): RawGroup[] {
  const out = buildMLeaderContextGroups(geom, legacyLineSpacingInExtraGroups);
  for (const sec of geom.secondaryContexts ?? []) {
    out.push(
      ...buildSecondaryContextBlock(
        sec.context,
        sec.leaderRoots,
        sec.rawContextGroups,
      ),
    );
  }
  return out;
}
